import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsError, FrontendErrorsStatus } from "@/features/observability/hooks/use-frontend-errors";
import { platformErrorsFilters, type PlatformErrorsFilters } from "@/features/observability/platform-errors/components/platform-errors-filters";
import { platformErrorsFormat } from "@/features/observability/platform-errors/components/platform-errors-format";
import { PlatformErrorsFilterBar } from "@/features/observability/platform-errors/components/PlatformErrorsFilterBar";
import { PlatformErrorsSeriesChart } from "@/features/observability/platform-errors/components/PlatformErrorsSeriesChart";
import { PlatformErrorsGroupsTable, platformErrorsGroupKey } from "@/features/observability/platform-errors/components/PlatformErrorsGroupsTable";
import { PlatformErrorsEventsDrawer } from "@/features/observability/platform-errors/components/PlatformErrorsEventsDrawer";
import {
    usePlatformErrorsEvents,
    usePlatformErrorsGroups,
    usePlatformErrorsSeries,
    usePlatformErrorsSummary,
} from "@/features/observability/platform-errors/hooks/use-platform-errors";
import type {
    PlatformErrorGroup,
    PlatformErrorsEventsParams,
    PlatformErrorsPopulationParams,
    PlatformErrorsSummary,
} from "@/features/observability/platform-errors/types/platform-errors.types";
import "@/features/observability/styles/frontend-errors.css";

const EVENTS_PAGE_SIZE = 50;

const isPending = (status: FrontendErrorsStatus): boolean => status === "loading" || status === "idle";

type Selection = { key: string; group: PlatformErrorGroup };

type SectionStateProps = {
    status: FrontendErrorsStatus;
    error: FrontendErrorsError | null;
    label: string;
    onRetry: () => void;
    onMoveWindow: () => void;
};

function SectionState({ status, error, label, onRetry, onMoveWindow }: SectionStateProps) {
    if (isPending(status)) return <div className="fe-section-state">{`Cargando ${label}…`}</div>;
    if (platformErrorsFormat.isOutsideRetention(error)) {
        return (
            <div className="fe-section-state">
                {`${platformErrorsFormat.OUTSIDE_RETENTION_TEXT}: no se puede mostrar ${label}.`}
                <button type="button" className="btn btn--sm" onClick={onMoveWindow}>Actualizar rango</button>
            </div>
        );
    }
    return (
        <div className="fe-section-state">
            {status === "unavailable" ? `Fuente no disponible: no se puede mostrar ${label}.` : `No se pudo cargar ${label}.`}
            <button type="button" className="btn btn--sm" onClick={onRetry}>Reintentar</button>
        </div>
    );
}

type Action = { label: string; onClick: () => void };

function Message({ icon, title, text, action }: { icon: string | null; title: string; text: string; action: Action | null }) {
    return (
        <div className="fe-card fe-message">
            {icon === null ? null : <span className="material-symbols-outlined fe-message__icon" aria-hidden="true">{icon}</span>}
            <div className="fe-message__title">{title}</div>
            <div className="fe-message__text">{text}</div>
            {action === null ? null : <button type="button" className="btn" onClick={action.onClick}>{action.label}</button>}
        </div>
    );
}

const staleText = (data: PlatformErrorsSummary): string => {
    const heartbeat = data.collector.checkedAt === null ? "sin dato" : frontendErrorsFormat.sinceAsOf(data.collector.checkedAt, data.asOf);
    const received = data.freshness === null || data.freshness.lastReceivedAt === null
        ? "sin dato"
        : frontendErrorsFormat.sinceAsOf(data.freshness.lastReceivedAt, data.asOf);
    return `Datos obsoletos · último latido ${heartbeat} · último recibido ${received}`;
};

function Kpis({ data, lowerBound }: { data: PlatformErrorsSummary; lowerBound: boolean }) {
    const top = data.byService === null || data.byService.buckets.length === 0 ? null : data.byService.buckets[0];
    const severities = data.bySeverity === null ? [] : data.bySeverity.buckets.filter((bucket) => bucket.count > 0);
    const minimum = lowerBound ? <span className="fe-kpi__unit">(mín.)</span> : null;
    return (
        <div className="fe-kpis">
            <div className="fe-kpi fe-kpi--wide" data-testid="pe-kpi-total">
                <div className="fe-kpi__label">Errores en el periodo</div>
                <div className="fe-kpi__value">
                    <span data-testid="pe-kpi-value" className="fe-kpi__num">{frontendErrorsFormat.count(data.total)}</span>
                    <span className="fe-kpi__unit">ocurrencias</span>
                    {minimum}
                </div>
                {severities.length > 0 ? (
                    <div className="fe-chips">
                        {severities.map((bucket) => {
                            const style = platformErrorsFormat.severityStyle(bucket.key);
                            return (
                                <span key={bucket.key} className="fe-chip">
                                    <span className="fe-swatch" style={{ background: style.color }} />
                                    {style.label}
                                    <b>{frontendErrorsFormat.count(bucket.count)}</b>
                                </span>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            <div className="fe-kpi" data-testid="pe-kpi-fingerprints">
                <div className="fe-kpi__label">Fingerprints distintos</div>
                <div className="fe-kpi__value">
                    <span data-testid="pe-kpi-value" className="fe-kpi__num">{frontendErrorsFormat.count(data.distinctFingerprints)}</span>
                    {minimum}
                </div>
                <div className="fe-kpi__note">tipos de fallo diferentes</div>
            </div>
            <div className="fe-kpi" data-testid="pe-kpi-top-service">
                <div className="fe-kpi__label">Servicio con más errores</div>
                <div className="fe-kpi__value">
                    <span className="fe-kpi__num fe-ellipsis">{top === null ? "—" : top.key}</span>
                </div>
                <div className="fe-kpi__note">{top === null ? "sin datos por servicio" : `${frontendErrorsFormat.count(top.count)} ocurrencias${lowerBound ? " (mín.)" : ""}`}</div>
            </div>
        </div>
    );
}

function Coverage({ data }: { data: PlatformErrorsSummary }) {
    const coverage = data.coverage;
    return (
        <div className="fe-card" data-testid="pe-coverage">
            <div className="fe-card__hd">
                <div className="fe-card__title">Cobertura</div>
            </div>
            {coverage === null || coverage.rejected === null ? (
                <div className="fe-section-state">Sin dato de cobertura para este periodo.</div>
            ) : (
                <div className="fe-stack">
                    <div>{`${frontendErrorsFormat.count(coverage.rejected)} registros rechazados en la ingesta (no se cuentan como errores)`}</div>
                    {coverage.byReason.buckets.map((bucket) => (
                        <div key={bucket.key} className="fe-muted">{`${bucket.key}: ${frontendErrorsFormat.count(bucket.count)}`}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function PlatformErrorsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => platformErrorsFilters.parse(searchParams), [searchParams]);
    const [anchor, setAnchor] = useState(() => Date.now());
    const resolved = useMemo(() => platformErrorsFilters.resolveRange(filters, anchor), [filters, anchor]);
    const range = resolved.range;
    const query = useMemo(() => (range === null ? null : platformErrorsFilters.toQuery(filters, range)), [filters, range]);

    // `/summary` fixes asOf and returns it; every other read of this view uses that same asOf.
    const summary = usePlatformErrorsSummary(query);
    const asOf = summary.data === null ? null : summary.data.asOf;
    const population = useMemo<PlatformErrorsPopulationParams | null>(
        // The retention floor is re-applied at every read: a view left open must not slide past the 13 days.
        () => (query === null || asOf === null
            ? null
            : { ...query, from: Math.max(query.from, platformErrorsFilters.retentionFloor(Date.now())), asOf }),
        [query, asOf],
    );
    const series = usePlatformErrorsSeries(population);
    const groups = usePlatformErrorsGroups(population);

    const populationKey = population === null ? null : JSON.stringify(population);
    const [selection, setSelection] = useState<Selection | null>(null);
    const selected = selection !== null && selection.key === populationKey ? selection.group : null;
    const selectedKey = selected === null ? null : platformErrorsGroupKey(selected);
    const eventsParams = useMemo<PlatformErrorsEventsParams | null>(() => {
        if (population === null || selected === null || selected.fingerprint === null || selected.service === null) return null;
        return {
            ...population,
            from: Math.max(population.from, platformErrorsFilters.retentionFloor(Date.now())),
            service: selected.service,
            fingerprint: selected.fingerprint,
            limit: EVENTS_PAGE_SIZE,
        };
    }, [population, selected]);
    const events = usePlatformErrorsEvents(eventsParams);

    const [lastAttemptAt, setLastAttemptAt] = useState<number | null>(null);
    useEffect(() => {
        if (summary.status === "unavailable") setLastAttemptAt(Date.now());
    }, [summary.status]);

    const changeFilters = (next: PlatformErrorsFilters): void => {
        if (next.range !== filters.range || next.customFrom !== filters.customFrom || next.customTo !== filters.customTo) {
            setAnchor(Date.now());
        }
        setSearchParams(platformErrorsFilters.toSearch(next));
    };

    // A new anchor only re-reads when it moves the window (presets, or a free range capped at now);
    // a past free range keeps its key, so it is asked again explicitly.
    const reload = (): void => {
        const now = Date.now();
        const next = platformErrorsFilters.resolveRange(filters, now).range;
        if (range !== null && next !== null && next.from === range.from && next.to === range.to) {
            summary.refresh();
            return;
        }
        setAnchor(now);
    };

    const openGroup = (group: PlatformErrorGroup): void => {
        if (populationKey !== null) setSelection({ key: populationKey, group });
    };
    const closeDrawer = useCallback((): void => setSelection(null), []);

    const forbidden = [summary.status, series.status, groups.status].some((status) => status === "forbidden");
    const data = summary.data;

    const header = (
        <>
            <PageHeader
                title="Errores de la plataforma"
                lede="/platform-errors"
                right={(
                    <button type="button" className="btn" onClick={reload} disabled={isPending(summary.status)}>
                        <span className="material-symbols-outlined" aria-hidden="true">autorenew</span>
                        {isPending(summary.status) ? "Actualizando…" : "Actualizar"}
                    </button>
                )}
            />
            <PlatformErrorsFilterBar filters={filters} range={range} retentionFloor={platformErrorsFilters.retentionFloor(anchor)} onChange={changeFilters} />
            {range !== null && resolved.clamped ? (
                <div className="fe-notice fe-notice--info" role="status">
                    <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    {`Rango recortado a lo consultable (máximo 7 días dentro de los últimos 13, sin futuro). Se muestra ${frontendErrorsFormat.dateTime(range.from)} → ${frontendErrorsFormat.dateTime(range.to)}.`}
                </div>
            ) : null}
            {range === null ? (
                <div className="fe-notice fe-notice--info" role="status">
                    <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    {resolved.clamped
                        ? "El rango queda fuera de los 13 días que se conservan para la plataforma. Elige otro rango."
                        : "Elige un rango válido: el inicio debe ser anterior al final."}
                </div>
            ) : null}
        </>
    );

    const body = (): React.ReactNode => {
        if (range === null) return null;
        if (forbidden) {
            return (
                <div className="fe-center">
                    <Message
                        icon="lock"
                        title="Tu usuario no tiene acceso a los errores de la plataforma"
                        text="No es un problema de datos. Pide acceso a quien administre VB Metrics."
                        action={null}
                    />
                </div>
            );
        }
        if (summary.status === "unavailable") {
            return (
                <div className="fe-stack">
                    <div className="fe-notice fe-notice--crit" role="alert">
                        <span className="material-symbols-outlined" aria-hidden="true">cloud_off</span>
                        {lastAttemptAt === null ? "Fuente no disponible" : `Fuente no disponible · último intento ${frontendErrorsFormat.dateTimeSeconds(lastAttemptAt)}`}
                    </div>
                    <Message
                        icon={null}
                        title="No se pueden mostrar los errores ahora"
                        text="La fuente no respondió. No hay datos para este periodo: esto no significa que no haya errores."
                        action={{ label: "Reintentar", onClick: summary.refresh }}
                    />
                </div>
            );
        }
        if (summary.status === "error") {
            if (platformErrorsFormat.isOutsideRetention(summary.error)) {
                return (
                    <Message
                        icon="history"
                        title={platformErrorsFormat.OUTSIDE_RETENTION_TEXT}
                        text="Mueve el rango a los días que se conservan."
                        action={{ label: "Actualizar rango", onClick: reload }}
                    />
                );
            }
            return (
                <Message
                    icon={null}
                    title="No se pudieron cargar los errores de la plataforma"
                    text="La respuesta no se pudo leer. Reintenta o avisa a ingeniería."
                    action={{ label: "Reintentar", onClick: summary.refresh }}
                />
            );
        }
        if (data === null) return <div className="fe-section-state">Cargando errores de la plataforma…</div>;

        const lowerBound = platformErrorsFormat.isLowerBound(data);
        // countsAreLowerBound is also true when only stale: that case is told once, by the stale banner.
        const partial = data.sourceStatus !== "ok" || (data.countsAreLowerBound && data.stale === false);
        const freshness = data.freshness === null ? null : data.freshness.lastReceivedAt;
        // Only a healthy, fresh answer can claim there were no errors.
        const empty = data.total === 0 && platformErrorsFormat.isLowerBound(data) === false && data.stale === false && data.sourceStatus === "ok";

        return (
            <div className="fe-stack">
                <div className="fe-strip">
                    <span className={`fe-strip__source fe-strip__source--${data.sourceStatus}`}>
                        <span className="fe-strip__dot" />
                        {data.sourceStatus === "ok" ? "Fuente OK" : "Fuente degradada"}
                    </span>
                    <span className="fe-strip__item">{`Entorno: ${data.environment}`}</span>
                    <span className="fe-strip__item">
                        <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
                        {frontendErrorsFormat.freshness(freshness, data.asOf)}
                    </span>
                    <span className="fe-muted">{`${frontendErrorsFormat.dateTime(data.from)} → ${frontendErrorsFormat.dateTime(data.to)}`}</span>
                </div>
                {data.stale ? (
                    <div className="fe-notice fe-notice--warn" role="status">
                        <span className="material-symbols-outlined" aria-hidden="true">history</span>
                        <span>{staleText(data)}</span>
                        <span className="fe-muted">La ingesta no confirma actividad reciente: puede haber errores que aún no se ven.</span>
                    </div>
                ) : null}
                {partial ? (
                    <div className="fe-notice fe-notice--warn" role="status">
                        <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                        Datos parciales: los conteos son un mínimo
                    </div>
                ) : null}
                {empty ? (
                    <div className="fe-empty">
                        <Message
                            icon="inbox"
                            title="Sin errores en este periodo"
                            text={`La fuente está al día y no registró errores entre ${frontendErrorsFormat.dateTime(data.from)} y ${frontendErrorsFormat.dateTime(data.to)}.`}
                            action={null}
                        />
                        <Coverage data={data} />
                    </div>
                ) : (
                    <>
                        <Kpis data={data} lowerBound={lowerBound} />
                        <div className="fe-row">
                            <div className="fe-card fe-row__main">
                                {series.data !== null && series.data.buckets !== null ? (
                                    <PlatformErrorsSeriesChart
                                        buckets={series.data.buckets}
                                        bucketMs={series.data.bucketMs}
                                        rangeMs={data.to - data.from}
                                        lowerBound={platformErrorsFormat.isLowerBound(series.data)}
                                    />
                                ) : null}
                                {series.data !== null && series.data.buckets === null ? (
                                    <div className="fe-section-state">La fuente no devolvió la serie para este periodo.</div>
                                ) : null}
                                {series.data === null ? <SectionState status={series.status} error={series.error} label="la serie" onRetry={series.refresh} onMoveWindow={reload} /> : null}
                            </div>
                            <Coverage data={data} />
                        </div>
                        {groups.data !== null && groups.data.groups !== null ? (
                            <PlatformErrorsGroupsTable
                                groups={groups.data.groups}
                                asOf={groups.data.asOf}
                                lowerBound={platformErrorsFormat.isLowerBound(groups.data)}
                                stale={groups.data.stale}
                                truncated={groups.data.reason === "groups_truncated"}
                                selectedKey={selectedKey}
                                onOpen={openGroup}
                            />
                        ) : null}
                        {groups.data !== null && groups.data.groups === null ? (
                            <div className="fe-card fe-section-state">Grupos no disponibles: la fuente no los devolvió para este periodo. Esto no significa que no haya errores.</div>
                        ) : null}
                        {groups.data === null ? (
                            <div className="fe-card">
                                <SectionState status={groups.status} error={groups.error} label="los grupos" onRetry={groups.refresh} onMoveWindow={reload} />
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    };

    return (
        <Shell title="Errores de la plataforma">
            <div className="frontend-errors">
                {header}
                {body()}
                {selected !== null && !forbidden ? <PlatformErrorsEventsDrawer group={selected} resource={events} onClose={closeDrawer} onMoveWindow={reload} /> : null}
            </div>
        </Shell>
    );
}
